import { pointLoadLimit } from './potreeState';

/** Represents a node stored in the LRU cache */
interface LRUNode {
  id: string | number;
  loaded: boolean;
  numPoints: number;
  children: Record<string, LRUNode>;
  unload?: () => void;
  unloadForLRU?: () => void;
  dispose?: () => void;
}

class LRUItem {
  previous: LRUItem | null;
  next: LRUItem | null;
  node: LRUNode;

  constructor(node: LRUNode) {
    this.previous = null;
    this.next = null;
    this.node = node;
  }
}

/**
 *
 * @class A doubly-linked-list of the least recently used elements.
 */
class LRU {
  // the least recently used item
  first: LRUItem | null;
  // the most recently used item
  last: LRUItem | null;
  // a list of all items in the lru list
  items: Record<string | number, LRUItem>;
  elements: number;
  numPoints: number;
  /** Nodes that are currently visible and must not be evicted by freeMemory */
  protected: Set<string | number>;

  constructor() {
    this.first = null;
    this.last = null;
    this.items = {};
    this.elements = 0;
    this.numPoints = 0;
    /** Nodes that are currently visible and must not be evicted by freeMemory */
    this.protected = new Set();
  }

  size() {
    return this.elements;
  }

  contains(node: LRUNode) {
    return this.items[node.id] == null;
  }

  touch(node: LRUNode) {
    if (!node.loaded) {
      return;
    }

    // Mark as protected — currently visible nodes must not be evicted
    this.protected.add(node.id);

    let item: LRUItem;
    if (this.items[node.id] == null) {
      // add to list
      item = new LRUItem(node);
      item.previous = this.last;
      this.last = item;
      if (item.previous !== null) {
        item.previous.next = item;
      }

      this.items[node.id] = item;
      this.elements++;

      if (this.first === null) {
        this.first = item;
      }
      this.numPoints += node.numPoints;
    } else {
      // update in list
      item = this.items[node.id];
      if (item.previous === null) {
        // handle touch on first element
        if (item.next !== null) {
          this.first = item.next;
          this.first.previous = null;
          item.previous = this.last;
          item.next = null;
          this.last = item;
          if (item.previous) item.previous.next = item;
        }
      } else if (item.next === null) {
        // handle touch on last element
      } else {
        // handle touch on any other element
        item.previous.next = item.next;
        item.next.previous = item.previous;
        item.previous = this.last;
        item.next = null;
        this.last = item;
        if (item.previous) item.previous.next = item;
      }
    }
  }

  remove(node: LRUNode) {
    const lruItem = this.items[node.id];
    if (lruItem) {
      if (this.elements === 1) {
        this.first = null;
        this.last = null;
      } else {
        if (!lruItem.previous) {
          this.first = lruItem.next;
          if (this.first) this.first.previous = null;
        }
        if (!lruItem.next) {
          this.last = lruItem.previous;
          if (this.last) this.last.next = null;
        }
        if (lruItem.previous && lruItem.next) {
          lruItem.previous.next = lruItem.next;
          lruItem.next.previous = lruItem.previous;
        }
      }

      delete this.items[node.id];
      this.elements--;
      this.numPoints -= node.numPoints;
    }
  }

  getLRUItem() {
    if (this.first === null) {
      return null;
    }
    const lru = this.first;

    return lru.node;
  }

  toString() {
    let string = '{ ';
    let curr = this.first;
    while (curr !== null) {
      string += curr.node.id;
      if (curr.next !== null) {
        string += ', ';
      }
      curr = curr.next;
    }
    string += '}';
    string += '(' + this.size() + ')';
    return string;
  }

  freeMemory() {
    if (this.elements <= 1) {
      return;
    }

    while (this.numPoints > pointLoadLimit) {
      // Find the first non-protected element to evict
      let element = this.first;
      while (element && this.protected.has(element.node.id)) {
        element = element.next;
      }
      if (!element) break;
      const node = element.node;
      this.disposeDescendants(node);
    }
  }

  // Pythagoras
  unload() {
    while (this.elements > 0) {
      const element = this.first;
      if (!element) break;
      const node = element.node;
      if (node.unload) {
        node.unload();
      }
      this.remove(node);
    }
  }

  /**
   * Dispose all nodes and clear the LRU cache.
   * Call this when removing a point cloud to free memory.
   */
  dispose() {
    while (this.elements > 0) {
      const element = this.first;
      if (!element) break;
      const node = element.node;
      if (node.dispose) {
        node.dispose();
      }
      this.remove(node);
    }
    this.items = {};
    this.first = null;
    this.last = null;
    this.elements = 0;
    this.numPoints = 0;
  }

  disposeDescendants(node: LRUNode) {
    const stack: LRUNode[] = [];
    stack.push(node);
    while (stack.length > 0) {
      const current = stack.pop();
      if (!current) continue;

      // Never evict nodes that are currently visible — evicting them causes
      // a visual LOD pop-in loop where geometry is freed and immediately reloaded.
      if (this.protected.has(current.id)) {
        continue;
      }

      // Use unloadForLRU if available (PPC nodes) to allow reloading when camera moves back
      // Otherwise fall back to dispose for other node types
      if (current.unloadForLRU) {
        current.unloadForLRU();
      } else if (current.dispose) {
        current.dispose();
      }
      this.remove(current);

      for (const key in current.children) {
        if (Object.prototype.hasOwnProperty.call(current.children, key)) {
          const child = current.children[key];
          if (child.loaded) {
            stack.push(current.children[key]);
          }
        }
      }
    }
  }
}

export { LRU, LRUItem };
export type { LRUNode };
